import React, { useState, useEffect } from 'react';
import { Search, Plus, Settings, Users, Shield, Globe, CheckCircle, Download, Upload, Eye, Link, Building, Mail, Key, Save } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';

const TenantManagementSSOConfiguration = () => {
  const [activeTab, setActiveTab] = useState('tenants');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddTenant, setShowAddTenant] = useState(false);
  const [showSSOConfig, setShowSSOConfig] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  // API Configuration state
  const [apiConfigs, setApiConfigs] = useState({
    resendApiKey: '',
    resendDomain: '',
    emailSenderName: 'Sedunia Travel Hub',
    emailSenderAddress: 'noreply@sedunia.com',
    supabaseUrl: '',
    supabaseAnonKey: ''
  });
  const [isApiConfigSaving, setIsApiConfigSaving] = useState(false);

  // Mock data for tenants
  const [tenants, setTenants] = useState([
    {
      id: 1,
      name: 'Acme Corporation',
      domain: 'acme.com',
      subdomain: 'acme',
      status: 'active',
      users: 245,
      subscription: 'Enterprise',
      ssoEnabled: true,
      lastActivity: '2024-01-15T10:30:00Z',
      createdAt: '2023-06-15T08:00:00Z',
      logo: '/api/placeholder/48/48'
    },
    {
      id: 2,
      name: 'Tech Innovations Ltd',
      domain: 'techinnovations.com',
      subdomain: 'techinno',
      status: 'active',
      users: 178,
      subscription: 'Professional',
      ssoEnabled: false,
      lastActivity: '2024-01-14T16:45:00Z',
      createdAt: '2023-08-20T09:15:00Z',
      logo: '/api/placeholder/48/48'
    },
    {
      id: 3,
      name: 'Global Enterprises',
      domain: 'globalent.org',
      subdomain: 'global',
      status: 'pending',
      users: 89,
      subscription: 'Starter',
      ssoEnabled: false,
      lastActivity: '2024-01-10T14:20:00Z',
      createdAt: '2024-01-05T11:30:00Z',
      logo: '/api/placeholder/48/48'
    }
  ]);

  // Mock data for SSO configurations
  const [ssoConfigs, setSsoConfigs] = useState([
    {
      id: 1,
      tenantId: 1,
      provider: 'Azure AD',
      status: 'active',
      domain: 'acme.com',
      lastSync: '2024-01-15T09:00:00Z',
      userCount: 245,
      protocol: 'SAML 2.0'
    },
    {
      id: 2,
      tenantId: 2,
      provider: 'Google Workspace',
      status: 'pending',
      domain: 'techinnovations.com',
      lastSync: null,
      userCount: 0,
      protocol: 'OAuth 2.0'
    }
  ]);

  const filteredTenants = tenants?.filter(tenant => {
    const matchesSearch = tenant?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         tenant?.domain?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesFilter = filterStatus === 'all' || tenant?.status === filterStatus;
    return matchesSearch && matchesFilter;
  }) || [];

  const handleBulkAction = (action) => {
    console.log('Bulk action:', action, 'for tenants:', selectedTenants);
    // Implement bulk actions
  };

  const handleApiConfigSave = async () => {
    setIsApiConfigSaving(true);
    try {
      // Here you would typically save to your backend/database
      // For now, we'll simulate the save
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('API configurations saved:', apiConfigs);
      // You could also show a success toast notification here
    } catch (error) {
      console.error('Failed to save API configurations:', error);
    } finally {
      setIsApiConfigSaving(false);
    }
  };

  const handleApiConfigChange = (field, value) => {
    setApiConfigs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const TenantCard = ({ tenant }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4">
          <Checkbox
            checked={selectedTenants?.includes(tenant?.id)}
            onChange={(checked) => {
              if (checked) {
                setSelectedTenants([...selectedTenants, tenant?.id]);
              } else {
                setSelectedTenants(selectedTenants?.filter(id => id !== tenant?.id));
              }
            }}
          />
          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
            <Building className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h3 className="text-lg font-semibold text-gray-900">{tenant?.name}</h3>
              <span className={`px-2 py-1 text-xs rounded-full ${
                tenant?.status === 'active' ? 'bg-green-100 text-green-800' :
                tenant?.status === 'pending'? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
              }`}>
                {tenant?.status}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-1">{tenant?.domain}</p>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
              <span className="flex items-center">
                <Users className="w-4 h-4 mr-1" />
                {tenant?.users} users
              </span>
              <span className="flex items-center">
                <Shield className={`w-4 h-4 mr-1 ${tenant?.ssoEnabled ? 'text-green-500' : 'text-gray-400'}`} />
                SSO {tenant?.ssoEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelectedTenant(tenant);
              setShowSSOConfig(true);
            }}
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => console.log('View tenant:', tenant?.id)}
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  const SSOConfigModal = () => {
    const [configStep, setConfigStep] = useState('provider');
    const [ssoProvider, setSsoProvider] = useState('');
    const [domainVerified, setDomainVerified] = useState(false);
    const [testConnection, setTestConnection] = useState(null);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                SSO Configuration - {selectedTenant?.name}
              </h2>
              <Button
                variant="ghost"
                onClick={() => setShowSSOConfig(false)}
              >
                ×
              </Button>
            </div>
            
            {/* Step indicator */}
            <div className="flex items-center space-x-4 mt-4">
              {['provider', 'domain', 'configure', 'test']?.map((step, index) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    configStep === step ? 'bg-blue-600 text-white' :
                    index < ['provider', 'domain', 'configure', 'test']?.indexOf(configStep) ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  {index < 3 && <div className="w-8 h-px bg-gray-300 ml-2" />}
                </div>
              ))}
            </div>
          </div>

          <div className="p-6">
            {configStep === 'provider' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Select SSO Provider</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { id: 'azure', name: 'Azure AD', icon: '🔷' },
                    { id: 'google', name: 'Google Workspace', icon: '🔍' },
                    { id: 'okta', name: 'Okta', icon: '⭕' },
                    { id: 'saml', name: 'Generic SAML', icon: '🔐' },
                    { id: 'oidc', name: 'OpenID Connect', icon: '🔑' },
                    { id: 'ldap', name: 'LDAP/AD', icon: '📁' }
                  ]?.map(provider => (
                    <div
                      key={provider?.id}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                        ssoProvider === provider?.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSsoProvider(provider?.id)}
                    >
                      <div className="text-2xl mb-2">{provider?.icon}</div>
                      <div className="font-medium text-sm">{provider?.name}</div>
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => setConfigStep('domain')}
                  disabled={!ssoProvider}
                  className="w-full"
                >
                  Continue to Domain Verification
                </Button>
              </div>
            )}

            {configStep === 'domain' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Domain Verification</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-4">
                    Add the following DNS record to verify domain ownership:
                  </p>
                  <div className="bg-white p-3 rounded border font-mono text-sm">
                    TXT record: _seduniaverify.{selectedTenant?.domain} = "verification-token-123456"
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded-full ${domainVerified ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                  <span className="text-sm text-gray-600">Domain verification status</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDomainVerified(true)}
                  >
                    Check Verification
                  </Button>
                </div>

                <Button
                  onClick={() => setConfigStep('configure')}
                  disabled={!domainVerified}
                  className="w-full"
                >
                  Continue to Configuration
                </Button>
              </div>
            )}

            {configStep === 'configure' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium">SSO Configuration</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Entity ID</label>
                    <Input placeholder="https://login.microsoftonline.com/tenant-id" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">SSO URL</label>
                    <Input placeholder="https://login.microsoftonline.com/tenant-id/saml2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Certificate</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                      <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Upload X.509 Certificate</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Attribute Mapping</label>
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <Input placeholder="Email" value="http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress" />
                        <span className="text-gray-400 self-center">→</span>
                        <Input placeholder="email" />
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setConfigStep('test')}
                  className="w-full"
                >
                  Save and Test Connection
                </Button>
              </div>
            )}

            {configStep === 'test' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium">Test SSO Connection</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded-full ${
                        testConnection === 'success' ? 'bg-green-500' :
                        testConnection === 'error'? 'bg-red-500' : 'bg-gray-300'
                      }`}></div>
                      <span className="font-medium">Connection Test</span>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setTestConnection('success')}
                    >
                      <Link className="w-4 h-4 mr-2" />
                      Test Connection
                    </Button>
                  </div>
                  
                  {testConnection === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-green-800 font-medium">Connection Successful!</p>
                      </div>
                      <p className="text-green-700 text-sm mt-1">
                        SSO configuration is working correctly. Users can now authenticate via {ssoProvider}.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSSOConfig(false)}
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      // Save configuration
                      setShowSSOConfig(false);
                    }}
                  >
                    Activate SSO
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const AddTenantModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Add New Tenant</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Organization Name</label>
              <Input placeholder="Acme Corporation" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Domain</label>
              <Input placeholder="acme.com" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subdomain</label>
              <Input placeholder="acme" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Subscription Plan</label>
              <Select options={[
                { value: 'starter', label: 'Starter' },
                { value: 'professional', label: 'Professional' },
                { value: 'enterprise', label: 'Enterprise' }
              ]} />
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <Button variant="outline" onClick={() => setShowAddTenant(false)}>
            Cancel
          </Button>
          <Button onClick={() => setShowAddTenant(false)}>
            Create Tenant
          </Button>
        </div>
      </div>
    </div>
  );

  const ApiConfigurationTab = () => (
    <div className="space-y-6">
      {/* Supabase API Configuration */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Supabase Database Configuration</h2>
              <p className="text-sm text-gray-600">Configure your Supabase project connection settings</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Supabase Project URL
              </label>
              <Input
                type="url"
                placeholder="https://your-project-ref.supabase.co"
                value={apiConfigs?.supabaseUrl}
                onChange={(e) => handleApiConfigChange('supabaseUrl', e?.target?.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your Supabase project URL from the project settings dashboard.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Supabase Anonymous Key
              </label>
              <Input
                type="password"
                placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                value={apiConfigs?.supabaseAnonKey}
                onChange={(e) => handleApiConfigChange('supabaseAnonKey', e?.target?.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your Supabase anonymous (public) key. This key is safe to use in your frontend application.
              </p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-900 mb-2">Getting Your Supabase Keys</h3>
            <ul className="text-sm text-green-800 space-y-1">
              <li>1. Go to your Supabase project dashboard at supabase.com</li>
              <li>2. Navigate to Settings → API</li>
              <li>3. Copy the Project URL and anon/public key</li>
              <li>4. Paste them in the fields above</li>
            </ul>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className={`w-3 h-3 rounded-full ${
                apiConfigs?.supabaseUrl && apiConfigs?.supabaseAnonKey ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <span>
                {apiConfigs?.supabaseUrl && apiConfigs?.supabaseAnonKey ? 'Database Connection Configured' : 'Database Configuration Required'}
              </span>
            </div>
            
            <Button
              onClick={handleApiConfigSave}
              disabled={isApiConfigSaving || !(apiConfigs?.supabaseUrl && apiConfigs?.supabaseAnonKey)}
              className="flex items-center space-x-2"
            >
              {isApiConfigSaving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Supabase Config</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Resend API Configuration */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Mail className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Resend Email API Configuration</h2>
              <p className="text-sm text-gray-600">Configure your Resend API settings for email services</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Resend API Key
              </label>
              <Input
                type="password"
                placeholder="re_xxxxxxxxxxxxxxxxx"
                value={apiConfigs?.resendApiKey}
                onChange={(e) => handleApiConfigChange('resendApiKey', e?.target?.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your Resend API key. This will be stored securely and used for sending emails.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-2" />
                Sending Domain
              </label>
              <Input
                placeholder="yourdomain.com"
                value={apiConfigs?.resendDomain}
                onChange={(e) => handleApiConfigChange('resendDomain', e?.target?.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                The verified domain you'll send emails from.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sender Name
              </label>
              <Input
                placeholder="Sedunia Travel Hub"
                value={apiConfigs?.emailSenderName}
                onChange={(e) => handleApiConfigChange('emailSenderName', e?.target?.value)}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sender Email Address
              </label>
              <Input
                type="email"
                placeholder="noreply@sedunia.com"
                value={apiConfigs?.emailSenderAddress}
                onChange={(e) => handleApiConfigChange('emailSenderAddress', e?.target?.value)}
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Getting Started with Resend</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>1. Sign up for a Resend account at resend.com</li>
              <li>2. Verify your sending domain</li>
              <li>3. Generate an API key from your dashboard</li>
              <li>4. Enter your API key and domain above</li>
            </ul>
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <div className={`w-3 h-3 rounded-full ${
                apiConfigs?.resendApiKey ? 'bg-green-500' : 'bg-gray-300'
              }`}></div>
              <span>
                {apiConfigs?.resendApiKey ? 'API Key Configured' : 'API Key Required'}
              </span>
            </div>
            
            <Button
              onClick={handleApiConfigSave}
              disabled={isApiConfigSaving || !apiConfigs?.resendApiKey}
              className="flex items-center space-x-2"
            >
              {isApiConfigSaving ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Email Config</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Email Templates Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Email Templates</h3>
          <p className="text-sm text-gray-600">Manage email templates for different notifications</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {[
            { id: 'booking_confirmation', name: 'Booking Confirmation', description: 'Sent when a booking is confirmed' },
            { id: 'travel_reminder', name: 'Travel Reminder', description: 'Sent before travel dates' },
            { id: 'policy_update', name: 'Travel Policy Update', description: 'Sent when travel policies change' },
            { id: 'password_reset', name: 'Password Reset', description: 'Sent when users request password reset' }
          ]?.map(template => (
            <div key={template?.id} className="p-4 flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">{template?.name}</h4>
                <p className="text-sm text-gray-600">{template?.description}</p>
              </div>
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Configure
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
              <div className="flex space-x-1">
                <Button
                  variant={activeTab === 'tenants' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('tenants')}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Tenants
                </Button>
                <Button
                  variant={activeTab === 'sso' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('sso')}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  SSO Configuration
                </Button>
                <Button
                  variant={activeTab === 'api' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('api')}
                >
                  <Key className="w-4 h-4 mr-2" />
                  API Configuration
                </Button>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowAddTenant(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Tenant
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'api' && <ApiConfigurationTab />}
        
        {activeTab === 'tenants' && (
          <>
            {/* Filters and Search */}
            <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                    <Input
                      placeholder="Search tenants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select
                    value={filterStatus}
                    onChange={(value) => setFilterStatus(value)}
                    options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'active', label: 'Active' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'suspended', label: 'Suspended' }
                    ]}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  {selectedTenants?.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        {selectedTenants?.length} selected
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('export')}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Export
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tenant Cards */}
            <div className="space-y-4">
              {filteredTenants?.map(tenant => (
                <TenantCard key={tenant?.id} tenant={tenant} />
              ))}
            </div>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Tenants</p>
                    <p className="text-3xl font-bold text-gray-900">{tenants?.length || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Active Tenants</p>
                    <p className="text-3xl font-bold text-green-600">
                      {tenants?.filter(t => t?.status === 'active')?.length || 0}
                    </p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">SSO Enabled</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {tenants?.filter(t => t?.ssoEnabled)?.length || 0}
                    </p>
                  </div>
                  <Shield className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {tenants?.reduce((sum, t) => sum + (t?.users || 0), 0) || 0}
                    </p>
                  </div>
                  <Globe className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'sso' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">SSO Configurations</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {ssoConfigs?.map(config => {
                  const tenant = tenants?.find(t => t?.id === config?.tenantId);
                  return (
                    <div key={config?.id} className="p-6 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-3 h-3 rounded-full ${
                          config?.status === 'active' ? 'bg-green-500' :
                          config?.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}></div>
                        <div>
                          <h3 className="font-medium text-gray-900">{tenant?.name}</h3>
                          <p className="text-sm text-gray-600">{config?.domain} • {config?.provider}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{config?.userCount} users</p>
                          <p className="text-xs text-gray-500">
                            {config?.lastSync ? `Last sync: ${new Date(config?.lastSync)?.toLocaleDateString()}` : 'Never synced'}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedTenant(tenant);
                            setShowSSOConfig(true);
                          }}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Modals */}
      {showAddTenant && <AddTenantModal />}
      {showSSOConfig && <SSOConfigModal />}
    </div>
  );
};

export default TenantManagementSSOConfiguration;